import { devLog, devError } from '../utils/devLogger';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  serverTimestamp,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { canManageSchedules, normalizeRole } from './roleManagementService';
import { withRetry } from '../utils/firebaseRetry';

/**
 * Enhanced Attendance Service
 * Manages attendance records with proper cross-collection relationships
 */
class AttendanceService {
  _toDateOnlyKey(value) {
    const dateObj = value?.toDate?.()
      ? value.toDate()
      : (value instanceof Date ? value : new Date(value || 0));
    if (!dateObj || Number.isNaN(dateObj.getTime())) return 'unknown-date';
    const year = dateObj.getUTCFullYear();
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  _attendanceRecordKey(record = {}) {
    const learnerId = record?.learnerId || 'unknown-learner';
    const sessionId = record?.sessionId || 'unknown-session';
    const dateKey = this._toDateOnlyKey(record?.date);
    return `${sessionId}__${learnerId}__${dateKey}`;
  }
  
  /**
   * Create a new session for attendance tracking
   */
  async createSession(sessionData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { success: false, error: 'User not authenticated' };
      }

      const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const currentUserRole = normalizeRole(currentUserDoc.data()?.role);
      if (!canManageSchedules(currentUserRole) && currentUserRole !== 'qa') {
        return { success: false, error: 'Only internal staff can create or edit schedules' };
      }

      const session = {
        ...sessionData,
        createdAt: serverTimestamp(),
        attendanceMarked: false,
        status: sessionData.status || 'scheduled'
      };
      
      const docRef = await addDoc(collection(db, 'sessions'), session);
      return { success: true, sessionId: docRef.id };
    } catch (error) {
      devError('Error creating session:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get sessions for a cohort
   */
  async getSessionsForCohort(cohortId, startDate = null, endDate = null) {
    try {
      let q = query(
        collection(db, 'sessions'),
        where('cohortId', '==', cohortId),
        orderBy('scheduledDate', 'desc')
      );

      if (startDate && endDate) {
        q = query(q, 
          where('scheduledDate', '>=', startDate),
          where('scheduledDate', '<=', endDate)
        );
      }

      const snapshot = await getDocs(q);
      return {
        success: true,
        sessions: snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          scheduledDate: doc.data().scheduledDate?.toDate(),
          actualDate: doc.data().actualDate?.toDate(),
          createdAt: doc.data().createdAt?.toDate()
        }))
      };
    } catch (error) {
      devError('Error getting sessions for cohort:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark attendance for a session
   */
  async markAttendance(sessionId, attendanceData, facilitatorId) {
    try {
      const batch = writeBatch(db);

      const existingAttendanceSnapshot = await getDocs(
        query(collection(db, 'attendance'), where('sessionId', '==', sessionId))
      );
      const existingByLearnerAndDate = new Map();
      const duplicateRefsToDelete = [];
      existingAttendanceSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const key = this._attendanceRecordKey(data);
        if (existingByLearnerAndDate.has(key)) {
          duplicateRefsToDelete.push(docSnap.ref);
          return;
        }
        existingByLearnerAndDate.set(key, docSnap);
      });
      
      // Update session to mark attendance as completed
      const sessionRef = doc(db, 'sessions', sessionId);
      batch.update(sessionRef, {
        attendanceMarked: true,
        attendanceMarkedAt: serverTimestamp(),
        attendanceMarkedBy: facilitatorId,
        status: 'completed'
      });

      // Create individual attendance records
      const attendanceRecords = [];
      
      for (const record of attendanceData) {
        const attendanceDoc = {
          sessionId: sessionId,
          learnerId: record.learnerId,
          status: record.status, // present, absent, excused, incomplete, self_paced, other
          notes: record.notes || '',
          markedAt: serverTimestamp(),
          markedBy: facilitatorId,
          cohortId: record.cohortId,
          moduleId: record.moduleId,
          date: record.date || serverTimestamp(),
          source: record.source || 'manual', // manual, teams-auto, teams-manual, jotform
          duration: record.duration || null,
          attendancePercentage: record.attendancePercentage || null,
          isLowAttendance: record.isLowAttendance || false,
          flagged: record.flagged || false
        };

        const recordKey = this._attendanceRecordKey(attendanceDoc);
        const existingDocSnap = existingByLearnerAndDate.get(recordKey);
        if (existingDocSnap) {
          batch.update(existingDocSnap.ref, {
            ...attendanceDoc,
            updatedAt: serverTimestamp(),
            updatedBy: facilitatorId
          });
          attendanceRecords.push({ id: existingDocSnap.id, ...attendanceDoc });
        } else {
          const attendanceRef = doc(collection(db, 'attendance'));
          batch.set(attendanceRef, attendanceDoc);
          attendanceRecords.push({ id: attendanceRef.id, ...attendanceDoc });
        }
      }

      duplicateRefsToDelete.forEach((refToDelete) => {
        batch.delete(refToDelete);
      });

      const validStatuses = ['present', 'absent', 'excused', 'incomplete', 'self_paced', 'other', 'not_marked'];
      attendanceData.forEach((record) => {
        if (!validStatuses.includes(record.status)) {
          devLog('AttendanceService: unexpected attendance status received', record.status);
        }
      });

      await withRetry(() => batch.commit());

      return {
        success: true,
        attendanceRecords,
        message: `Attendance marked for ${attendanceData.length} learners`
      };
    } catch (error) {
      devError('Error marking attendance:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get attendance records for a session
   */
  async getSessionAttendance(sessionId) {
    try {
      const q = query(
        collection(db, 'attendance'),
        where('sessionId', '==', sessionId)
      );

      const snapshot = await getDocs(q);
      return {
        success: true,
        attendance: snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          markedAt: doc.data().markedAt?.toDate(),
          date: doc.data().date?.toDate()
        }))
      };
    } catch (error) {
      devError('Error getting session attendance:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get attendance summary for a learner
   */
  async getLearnerAttendanceSummary(learnerId, cohortId = null, startDate = null, endDate = null) {
    try {
      let q = query(
        collection(db, 'attendance'),
        where('learnerId', '==', learnerId)
      );

      if (cohortId) {
        q = query(q, where('cohortId', '==', cohortId));
      }

      if (startDate && endDate) {
        q = query(q, 
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const rawRecords = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        markedAt: doc.data().markedAt?.toDate(),
        date: doc.data().date?.toDate()
      }));
      const dedupedMap = new Map();
      rawRecords.forEach((record) => {
        const dayKey = record.date instanceof Date && !Number.isNaN(record.date.getTime())
          ? `${record.date.getFullYear()}-${String(record.date.getMonth() + 1).padStart(2, '0')}-${String(record.date.getDate()).padStart(2, '0')}`
          : 'unknown-date';
        const key = `${record.sessionId || record.moduleId || 'unknown-module'}_${dayKey}`;
        const existing = dedupedMap.get(key);
        const existingTime = existing?.markedAt?.getTime?.() || existing?.date?.getTime?.() || 0;
        const nextTime = record?.markedAt?.getTime?.() || record?.date?.getTime?.() || 0;
        if (!existing || nextTime >= existingTime) {
          dedupedMap.set(key, record);
        }
      });
      const records = Array.from(dedupedMap.values());

      // Calculate summary statistics
      const totalSessions = records.length;
      const presentCount = records.filter(r => r.status === 'present').length;
      const absentCount = records.filter(r => r.status === 'absent').length;
      const excusedCount = records.filter(r => r.status === 'excused').length;
      const incompleteCount = records.filter(r => r.status === 'incomplete').length;
      const selfPacedCount = records.filter(r => r.status === 'self_paced').length;
      const otherCount = records.filter(r => r.status === 'other').length;
      const lowAttendanceCount = records.filter(r => r.isLowAttendance).length;
      const flaggedCount = records.filter(r => r.flagged).length;

      // Calculate attendance rate excluding 'not marked' and 'incomplete' entries
      const markedSessions = records.filter(
        r => !['not_marked', 'incomplete', 'self_paced', 'other'].includes(r.status)
      ).length;
      const effectiveAttendance = records.filter(r => r.status === 'present' || r.status === 'excused').length;
      const attendanceRate = markedSessions > 0 ? Math.round((effectiveAttendance / markedSessions) * 100) : 0;

      return {
        success: true,
        summary: {
          totalSessions,
          markedSessions,
          presentCount,
          absentCount,
          excusedCount,
          incompleteCount,
          selfPacedCount,
          otherCount,
          lowAttendanceCount,
          flaggedCount,
          attendanceRate,
          effectiveAttendanceRate: Math.round((presentCount / markedSessions) * 100) || 0
        },
        records
      };
    } catch (error) {
      devError('Error getting learner attendance summary:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get pending attendance confirmations for a learner
   */
  async getPendingSessionsForLearner(learnerId, cohortId) {
    try {
      if (!learnerId) {
        return { success: true, sessions: [] };
      }

      const now = new Date();
      const nowTimestamp = Timestamp.fromDate(now);
      let learnerEmail = null;

      try {
        const learnerDoc = await getDoc(doc(db, 'users', learnerId));
        learnerEmail = learnerDoc.data()?.email || null;
      } catch (error) {
        devLog('Unable to load learner email for attendance confirmation:', error);
      }

      const sessionsMap = new Map();
      const sessionsRef = collection(db, 'sessions');
      const queries = [];

      if (cohortId) {
        queries.push(query(
          sessionsRef,
          where('cohortId', '==', cohortId),
          where('requiresAttendanceConfirmation', '==', true),
          where('confirmationRequested', '==', true),
          where('scheduledDate', '<=', nowTimestamp),
          orderBy('scheduledDate', 'desc')
        ));
      }

      queries.push(query(
        sessionsRef,
        where('assignedUserIds', 'array-contains', learnerId),
        where('requiresAttendanceConfirmation', '==', true),
        where('confirmationRequested', '==', true),
        where('scheduledDate', '<=', nowTimestamp),
        orderBy('scheduledDate', 'desc')
      ));

      if (learnerEmail) {
        queries.push(query(
          sessionsRef,
          where('assignedUserEmails', 'array-contains', learnerEmail.toLowerCase()),
          where('requiresAttendanceConfirmation', '==', true),
          where('confirmationRequested', '==', true),
          where('scheduledDate', '<=', nowTimestamp),
          orderBy('scheduledDate', 'desc')
        ));
      }

      for (const queryRef of queries) {
        try {
          const snapshot = await getDocs(queryRef);
          snapshot.docs.forEach((docSnap) => {
            sessionsMap.set(docSnap.id, {
              id: docSnap.id,
              ...docSnap.data(),
              scheduledDate: docSnap.data().scheduledDate?.toDate()
            });
          });
        } catch (error) {
          devError('Error loading attendance confirmation sessions:', error);
        }
      }

      const sessions = Array.from(sessionsMap.values());

      const attendanceQuery = query(
        collection(db, 'attendance'),
        where('learnerId', '==', learnerId)
      );
      const attendanceSnapshot = await getDocs(attendanceQuery);
      const attendedSessionIds = new Set(
        attendanceSnapshot.docs.map(doc => doc.data().sessionId)
      );

      const pendingSessions = sessions.filter(session => !attendedSessionIds.has(session.id));

      return { success: true, sessions: pendingSessions };
    } catch (error) {
      devError('Error getting pending sessions:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get attendance summary for a cohort
   */
  async getCohortAttendanceSummary(cohortId, startDate = null, endDate = null) {
    try {
      let q = query(
        collection(db, 'attendance'),
        where('cohortId', '==', cohortId)
      );

      if (startDate && endDate) {
        q = query(q,
          where('date', '>=', startDate),
          where('date', '<=', endDate)
        );
      }

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        markedAt: doc.data().markedAt?.toDate(),
        date: doc.data().date?.toDate()
      }));

      // Group by learner
      const learnerStats = {};
      const sessionStats = {};

      records.forEach(record => {
        // Learner statistics
        if (!learnerStats[record.learnerId]) {
          learnerStats[record.learnerId] = {
            present: 0,
            absent: 0,
            excused: 0,
            incomplete: 0,
            self_paced: 0,
            other: 0,
            lowAttendance: 0,
            flagged: 0,
            total: 0
          };
        }
        if (typeof learnerStats[record.learnerId][record.status] !== 'number') {
          learnerStats[record.learnerId][record.status] = 0;
        }
        learnerStats[record.learnerId][record.status]++;
        learnerStats[record.learnerId].total++;
        if (record.isLowAttendance) learnerStats[record.learnerId].lowAttendance++;
        if (record.flagged) learnerStats[record.learnerId].flagged++;

        // Session statistics
        if (!sessionStats[record.sessionId]) {
          sessionStats[record.sessionId] = {
            present: 0,
            absent: 0,
            excused: 0,
            incomplete: 0,
            self_paced: 0,
            other: 0,
            lowAttendance: 0,
            flagged: 0,
            total: 0,
            date: record.date
          };
        }
        if (typeof sessionStats[record.sessionId][record.status] !== 'number') {
          sessionStats[record.sessionId][record.status] = 0;
        }
        sessionStats[record.sessionId][record.status]++;
        sessionStats[record.sessionId].total++;
        if (record.isLowAttendance) sessionStats[record.sessionId].lowAttendance++;
        if (record.flagged) sessionStats[record.sessionId].flagged++;
      });

      // Calculate overall cohort statistics
      const totalRecords = records.length;
      const markedRecords = records.filter(
        r => !['not_marked', 'incomplete', 'self_paced', 'other'].includes(r.status)
      );
      const overallStats = {
        totalRecords,
        markedRecords: markedRecords.length,
        presentCount: records.filter(r => r.status === 'present').length,
        absentCount: records.filter(r => r.status === 'absent').length,
        excusedCount: records.filter(r => r.status === 'excused').length,
        incompleteCount: records.filter(r => r.status === 'incomplete').length,
        selfPacedCount: records.filter(r => r.status === 'self_paced').length,
        otherCount: records.filter(r => r.status === 'other').length,
        lowAttendanceCount: records.filter(r => r.isLowAttendance).length,
        flaggedCount: records.filter(r => r.flagged).length
      };
      
      // Calculate attendance rate excluding 'not marked' entries
      const effectiveAttendance = overallStats.presentCount + overallStats.excusedCount;
      overallStats.attendanceRate = markedRecords.length > 0
        ? Math.round((effectiveAttendance / markedRecords.length) * 100)
        : 0;
      overallStats.effectiveAttendanceRate = markedRecords.length > 0
        ? Math.round((overallStats.presentCount / markedRecords.length) * 100)
        : 0;

      return {
        success: true,
        summary: {
          overall: overallStats,
          byLearner: learnerStats,
          bySession: sessionStats,
          totalSessions: Object.keys(sessionStats).length,
          totalLearners: Object.keys(learnerStats).length
        },
        records
      };
    } catch (error) {
      devError('Error getting cohort attendance summary:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get attendance trends for reporting
   */
  async getAttendanceTrends(cohortId, period = 'monthly') {
    try {
      const q = query(
        collection(db, 'attendance'),
        where('cohortId', '==', cohortId),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate()
      }));

      // Group by time period
      const trends = {};
      
      records.forEach(record => {
        if (!record.date) return;
        
        let periodKey;
        if (period === 'weekly') {
          const weekStart = new Date(record.date);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          periodKey = weekStart.toISOString().split('T')[0];
        } else if (period === 'daily') {
          periodKey = record.date.toISOString().split('T')[0];
        } else { // monthly
          periodKey = `${record.date.getFullYear()}-${String(record.date.getMonth() + 1).padStart(2, '0')}`;
        }

        if (!trends[periodKey]) {
          trends[periodKey] = {
            period: periodKey,
            present: 0,
            absent: 0,
            excused: 0,
            incomplete: 0,
            self_paced: 0,
            other: 0,
            lowAttendance: 0,
            flagged: 0,
            total: 0,
            marked: 0
          };
        }

        if (typeof trends[periodKey][record.status] !== 'number') {
          trends[periodKey][record.status] = 0;
        }
        trends[periodKey][record.status]++;
        trends[periodKey].total++;
        if (!['not_marked', 'incomplete', 'self_paced', 'other'].includes(record.status)) {
          trends[periodKey].marked++;
        }
        if (record.isLowAttendance) trends[periodKey].lowAttendance++;
        if (record.flagged) trends[periodKey].flagged++;
      });

      // Calculate attendance rates
      Object.keys(trends).forEach(key => {
        const trend = trends[key];
        const effectiveAttendance = trend.present + trend.excused;
        trend.attendanceRate = trend.marked > 0
          ? Math.round((effectiveAttendance / trend.marked) * 100)
          : 0;
        trend.effectiveAttendanceRate = trend.marked > 0
          ? Math.round((trend.present / trend.marked) * 100)
          : 0;
      });

      return {
        success: true,
        trends: Object.values(trends).sort((a, b) => a.period.localeCompare(b.period))
      };
    } catch (error) {
      devError('Error getting attendance trends:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update attendance record
   */
  async updateAttendanceRecord(attendanceId, updates, facilitatorId) {
    try {
      const attendanceRef = doc(db, 'attendance', attendanceId);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: facilitatorId
      };

      await withRetry(() => updateDoc(attendanceRef, updateData));

      return {
        success: true,
        message: 'Attendance record updated successfully'
      };
    } catch (error) {
      devError('Error updating attendance record:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete session and associated attendance records
   */
  async deleteSession(sessionId, facilitatorId) {
    try {
      const batch = writeBatch(db);

      // Delete session
      const sessionRef = doc(db, 'sessions', sessionId);
      batch.delete(sessionRef);

      // Delete associated attendance records
      const attendanceQuery = query(
        collection(db, 'attendance'),
        where('sessionId', '==', sessionId)
      );
      
      const attendanceSnapshot = await getDocs(attendanceQuery);
      attendanceSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();

      return { 
        success: true, 
        message: 'Session and attendance records deleted successfully' 
      };
    } catch (error) {
      devError('Error deleting session:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Allows a learner to submit their own attendance for a past session.
   * @param {string} sessionId - The ID of the session.
   * @param {string} learnerId - The ID of the learner.
   * @param {'present' | 'absent' | 'excused'} status - The attendance status.
   * @param {string} [notes] - Optional notes from the learner.
   */
  async submitLearnerSelfAttendance(sessionId, learnerId, status, notes = '') {
    try {
      // 1. Fetch the session details
      const sessionDoc = await getDoc(doc(db, 'sessions', sessionId));
      if (!sessionDoc.exists) {
        return { success: false, error: 'Session not found.' };
      }
      const sessionData = sessionDoc.data();

      // 2. Validate that the session has passed
      const scheduledDateTime = sessionData.scheduledDate?.toDate();
      if (!scheduledDateTime || scheduledDateTime > new Date()) {
        return { success: false, error: 'Cannot log attendance for a future or ongoing session.' };
      }

      // 3. Validate that the learner is assigned to the session's cohort/program
      const learnerDoc = await getDoc(doc(db, 'users', learnerId));
      if (!learnerDoc.exists) {
        return { success: false, error: 'Learner not found.' };
      }
      const learnerData = learnerDoc.data();

      if (sessionData.cohortId && sessionData.cohortId !== learnerData.cohortId) {
        return { success: false, error: 'Learner is not assigned to this session\'s cohort.' };
      }
      // Add more checks if sessions can be program-specific without cohort
      // For now, assuming cohortId is the primary link for learners

      // 4. Check if an attendance record already exists for this learner and session
      const existingAttendanceQuery = query(
        collection(db, 'attendance'),
        where('sessionId', '==', sessionId),
        where('learnerId', '==', learnerId)
      );
      const existingAttendanceSnapshot = await getDocs(existingAttendanceQuery);

      if (!existingAttendanceSnapshot.empty) {
        // If an attendance record exists, we can update it if it was 'not_marked' or allow re-submission
        // For simplicity, let's prevent re-submission for now, or allow updating 'not_marked'
        const existingRecord = existingAttendanceSnapshot.docs[0];
        if (existingRecord.data().status !== 'not_marked') {
          return { success: false, error: 'Attendance already logged for this session.' };
        }
        // If it was 'not_marked', we can update it
        await updateDoc(existingRecord.ref, {
          status: status,
          notes: notes,
          markedAt: serverTimestamp(),
          source: 'learner-self-marked'
        });
        return { success: true, message: 'Attendance updated successfully.' };
      }

      // 5. Create a new attendance record
      const attendanceDoc = {
        sessionId: sessionId,
        learnerId: learnerId,
        status: status, // present, absent, excused
        notes: notes,
        markedAt: serverTimestamp(),
        markedBy: learnerId, // Learner marks themselves
        cohortId: sessionData.cohortId || null,
        moduleId: sessionData.moduleId || null,
        date: sessionData.scheduledDate || serverTimestamp(),
        source: 'learner-self-marked',
        duration: null, // Not applicable for self-marked
        attendancePercentage: null, // Not applicable for self-marked
        isLowAttendance: false,
        flagged: false
      };

      await withRetry(() => addDoc(collection(db, 'attendance'), attendanceDoc));

      return { success: true, message: 'Attendance logged successfully.' };
    } catch (error) {
      devError('Error submitting learner self-attendance:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new AttendanceService();
